import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  useFetcher,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "@remix-run/react";
import { recalculateOrder, resLineToCart } from "~/helpers/cartHelpers";
import { lineInBundle } from "~/helpers/promotionHelpers";
import SiteContext from "./SiteContext";
import {
  CartLine,
  DeliveryInfo,
  OrderFulfillmentType,
  OrderSource,
  OrderType,
  ResponseOrderWithCartLines,
  ResponseOrderWithLines,
} from "~/entities/orderEntities";
import MenuContext from "./MenuContext";
import { EMPTY_CUSTOMER, EMPTY_ORDER_RESPONSE } from "~/api/endpoints/Order";
import TaxContext from "~/contexts/TaxContext";
import {
  getOrderType,
  getPromotionsRequiringSelection,
  PromotionRequiringSelection,
} from "~/helpers/orderHelper";
import { BaseUser } from "~/entities/authEntities";
import { getRouteEnd, urlGen } from "~/helpers/routesHelpers";
import { KEYS } from "~/modules/checkout/constants";
import { SetAsideCart } from "~/helpers/sourceOrders";
import { toZonedTime } from "date-fns-tz";
import { isSameDay } from "date-fns";
import { formatTime } from "~/helpers/dateHelper";
import { leadNoteFor, settingsFor } from "~/helpers/leadTime";
import PromotionContext from "~/contexts/PromotionContextProvider";
import ChangeOrderModal from "~/modules/order/components/ChangeOrderModal/ChangeOrderModal";
import AuthContext from "./AuthContext";
import ConfirmActionModal from "~/modules/order/components/ConfirmActionModal/ConfirmActionModal";
import StoreFinder from "~/modules/sites/components/StoreFinder/StoreFinder";
import StoreFinderShell from "~/modules/sites/components/StoreFinder/StoreFinderShell";
import ChooseTime from "~/modules/order/components/ChooseTime/ChooseTime";
import ChooseTimeSheet from "~/modules/order/components/ChooseTime/ChooseTimeSheet";
import ThemeContext from "~/contexts/ThemeContext";
import {
  deliverySearchEnabled,
  storeFinderEnabled,
} from "~/helpers/storeFinderGate";
import {
  findOrderTypeForFulfilment,
  fulfilmentFromOrderType,
} from "~/helpers/storeFinderFulfilment";
import { OrgSites, SiteListObject } from "~/entities/siteEntities";
import { getOptionalJSONNullStrFormValue } from "~/helpers/formHelpers";

const OrderContext = createContext<{
  orderSummary: ResponseOrderWithCartLines;
  orderSource: OrderSource;
  orderTypeCode: string;
  orderType?: OrderType;
  deliveryInfo: DeliveryInfo | null;
  isTableOrdering: boolean;
  setTableNumber: (value: string) => void;
  getOrderLineById: (id: string) => CartLine | null | undefined;
  addOrderLine: (line: CartLine) => void;
  removeOrderLine: (id: string) => void;
  removeOrderLines: (ids: string[]) => void;
  // Adds lines in one save without merging them into existing lines, optionally removing others in the same save.
  // Used by the deal builder: lines added together in one save are the ones the server builds the deal from.
  addOrderLines: (lines: CartLine[], removeIds?: string[]) => void;
  updateOrderLine: (line: CartLine) => void;
  reset: () => void;
  refresh: () => void;
  setCustomerData: (x: BaseUser, allowSavePersonalData?: boolean) => void;
  redeemPromotion: (key: string) => void;
  removePromotion: () => void;
  redeemPromotionCode: (code: string) => void;
  removePromotionCode: () => void;
  setOrderTypeInfo: (
    orderType: OrderType,
    reqTime: string | null,
    deliveryInfo: DeliveryInfo | null,
    resetOrder: boolean,
  ) => void;
  numTimeSlots: number;
  // The earliest bookable time (ISO), or null when only ASAP or nothing is offered
  firstAvailableSlot: string | null;
  successPageOrderInfo: ResponseOrderWithLines | null;
  openOrderTimeModal: VoidFunction;
  /**
   * Opens the unified store finder. A no-op unless the org's template has
   * `storesScreen.finder.enabled` — see `~/helpers/storeFinderGate`. Callers
   * do not need to check the gate themselves; the overlay is also not
   * rendered at all when it is off, so a missed call site does nothing
   * rather than opening a finder the merchant never enabled.
   */
  // Optionally in another experience: the catering picker from everyday ordering
  openStoreFinder: (
    initialFulfilment?: "PICKUP" | "DELIVERY",
    experience?: OrderSource,
  ) => void;
  /**
   * Opens the "When would you like it?" time picker. Gated on the same
   * template switch as the store finder: the two are one redesigned entry
   * flow, and half of it would be worse than neither. A no-op when off — the
   * order-type modal still owns the time step there.
   */
  openTimeSheet: VoidFunction;
  promotionsRequiringSelection: PromotionRequiringSelection[];
  setGroupOrder: (val: boolean, redirectToPayment: boolean) => void;
  orderLoading: boolean;
  // No save in flight, the order shown is the server's
  orderIdle: boolean;
  setConfirmingAction: (x: ConfirmingAction | null) => void;
  readonlyCart: boolean;
  asapWait: number | null;
  signBackInRequired: boolean;
  sessionRequestedTime: string | null | undefined;
  // The cart just set aside by switching experience or store, shown once ("Your everyday cart is saved")
  savedCart: SetAsideCart | null;
  // The other experience's most recent cart (the everyday one while in catering), for the way back to it
  otherExperienceCart: SetAsideCart | null;
}>({
  orderSummary: EMPTY_ORDER_RESPONSE,
  orderSource: OrderSource.ONLINE,
  orderTypeCode: "",
  deliveryInfo: null,
  isTableOrdering: false,
  setTableNumber: () => null,
  getOrderLineById: () => null,
  addOrderLine: () => null,
  removeOrderLine: () => null,
  removeOrderLines: () => null,
  addOrderLines: () => null,
  updateOrderLine: () => null,
  reset: () => null,
  refresh: () => null,
  setCustomerData: () => null,
  redeemPromotion: () => null,
  removePromotion: () => null,
  redeemPromotionCode: () => null,
  removePromotionCode: () => null,
  setOrderTypeInfo: () => null,
  numTimeSlots: 0,
  firstAvailableSlot: null,
  successPageOrderInfo: null,
  openOrderTimeModal: () => null,
  openStoreFinder: () => null,
  openTimeSheet: () => null,
  promotionsRequiringSelection: [],
  setGroupOrder: () => null,
  orderLoading: true,
  orderIdle: false,
  setConfirmingAction: () => null,
  readonlyCart: true,
  asapWait: null,
  signBackInRequired: false,
  sessionRequestedTime: null,
  savedCart: null,
  otherExperienceCart: null,
});

export interface ConfirmingAction {
  action: () => any;
  icon: () => JSX.Element;
  title: string;
  body: string;
}

export const OrderContextProvider: React.FC<
  PropsWithChildren<{
    orderInfo: ResponseOrderWithLines | null;
    clearOrder: boolean;
    orderSource: OrderSource;
    orderTypeCode: string;
    deliveryInfo: DeliveryInfo | null;
    paramOrderSource: string;
    paramMenuCode?: string;
    availableTimeSlots: (string | null)[];
    requestedReadyTime?: string | null;
    tableNumber?: string | null;
    savedCustomer?: BaseUser;
    timeValid: boolean;
    asapWait: number | null;
    signBackInRequired: boolean;
    sessionRequestedTime: string | null | undefined;
    asapAvailable: boolean;
    savedCart?: SetAsideCart | null;
    otherExperienceCart?: SetAsideCart | null;
  }>
> = ({
  children,
  savedCustomer,
  orderInfo: unclearedOrderInfo,
  clearOrder,
  orderSource,
  orderTypeCode: loaderOrderTypeCode,
  deliveryInfo: loaderDeliveryInfo,
  paramOrderSource,
  paramMenuCode,
  availableTimeSlots,
  requestedReadyTime,
  tableNumber,
  timeValid,
  asapWait,
  signBackInRequired,
  sessionRequestedTime,
  asapAvailable,
  savedCart = null,
  otherExperienceCart = null,
}) => {
  const { scheduledPromotions } = useContext(PromotionContext);
  const [showOrderTimeModal, setShowOrderTimeModal] = useState(false);
  const [showStoreFinder, setShowStoreFinder] = useState(false);
  const [showTimeSheet, setShowTimeSheet] = useState(false);
  /**
   * The order type and delivery info the finder just chose, held for the time
   * sheet that opens straight after it.
   *
   * Without this the sheet's save reads `orderType` and `deliveryInfo` from
   * context, which still describe the PREVIOUS choice — the pickup post has not
   * round-tripped yet — so saving a time re-applied delivery wholesale and the
   * switch appeared never to happen. Null when the sheet is opened on its own
   * (the ETA chip), where context is the right source.
   */
  const [pendingTimeChoice, setPendingTimeChoice] = useState<{
    orderType: OrderType;
    deliveryInfo: DeliveryInfo | null;
  } | null>(null);
  const [storeFinderFulfilmentOverride, setStoreFinderFulfilmentOverride] =
    useState<"PICKUP" | "DELIVERY" | null>(null);
  const [confirmingAction, setConfirmingAction] =
    useState<ConfirmingAction | null>(null);
  const { site, orgId } = useContext(SiteContext);
  const { taxFromCode } = useContext(TaxContext);
  const { menu } = useContext(MenuContext);
  const { useComoAuth } = useContext(AuthContext);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fetcher = useFetcher({ key: KEYS.SUBMIT_ORDER_FETCHER });
  // Site list for the finder. Loaded lazily on first open, so orgs with the
  // finder switched off never pay for it.
  const orgSitesFetcher = useFetcher<OrgSites>();

  const { table: paramTableNumber } = useParams();

  const isTableOrdering = orderSource === OrderSource.TABLE_ORDERING;
  const defaultTableNumber = paramTableNumber ?? tableNumber;

  // Get the order type code from the fetcher request data, but only for requests where it is specified
  // (i.e. requests to change order type)
  const orderTypeCode = useMemo(() => {
    if (fetcher.state === "idle") {
      // When fetcher state is idle however, force getting the value from the loader, to prevent issues where the session is
      // not getting set correctly from being hidden / harder to reproduce.
      return loaderOrderTypeCode;
    } else {
      return (
        fetcher.formData?.get("orderTypeCode")?.toString() ??
        loaderOrderTypeCode
      );
    }
  }, [loaderOrderTypeCode, fetcher.state, fetcher.formData]);

  const orderSummary: ResponseOrderWithCartLines = useMemo(() => {
    let useLoaderOrderInfo = false;
    let actualClearOrder = clearOrder;
    let currentlySettingRequestedTime: string | null | undefined;

    if (fetcher.state === "idle") {
      // Loader data is up to date - get data from that
      useLoaderOrderInfo = true;
    } else if (fetcher.formData?.get("isReset")) {
      // If currently resetting the order, override clearOrder to true
      useLoaderOrderInfo = true;
      actualClearOrder = true;
      // Get requested time currently being set (when being set), so changes to requested ready time can optimistically update
      currentlySettingRequestedTime =
        fetcher.formData &&
        getOptionalJSONNullStrFormValue(fetcher.formData, "setRequestedTime");
    }

    if (useLoaderOrderInfo) {
      if (unclearedOrderInfo && !actualClearOrder) {
        if (!menu?.tabs) {
          // Hide lines when no menu data (as can't map to cart structure)
          console.warn("Menu or menu tabs not available");
          return { ...unclearedOrderInfo, lines: [] };
        }

        // Get order from api response
        return {
          ...unclearedOrderInfo,
          // An address chosen on /sites lands in the session, but an order that
          // already existed was built by the API without it and would keep
          // reporting none — the next write would post the server's null straight
          // back. Fall back to the session's address so the next order write
          // carries it and the backend can compute the delivery distance.
          // The order's own address always wins once it has one.
          deliveryInfo: unclearedOrderInfo.deliveryInfo ?? loaderDeliveryInfo,
          lines: unclearedOrderInfo.lines
            .map((line) => resLineToCart(line, menu))
            .filter((x) => !!x),
        };
      }

      // Generate empty order
      return {
        ...EMPTY_ORDER_RESPONSE,
        orderTypeCode,
        ...(isTableOrdering && { tableNumber: defaultTableNumber }),
        customer: savedCustomer ?? EMPTY_CUSTOMER, // initialise customer from session data only for creating initial order
        requestedReadyTime:
          currentlySettingRequestedTime !== undefined
            ? currentlySettingRequestedTime
            : (requestedReadyTime ?? null),
        deliveryInfo: loaderDeliveryInfo,
        lines: [],
        groupOrder: isTableOrdering,
      };
    }

    // Action submitted but loaders not loaded yet
    // Get data from action response
    if (fetcher.state === "loading") {
      const data: any = fetcher.data;
      if (data?.resOrder) {
        const orderInfo: ResponseOrderWithLines = data.resOrder;
        if (!menu?.tabs) {
          // Hide lines when no menu data (as can't map to cart structure)
          console.warn("Menu or menu tabs not available");
          return { ...orderInfo, lines: [] };
        }
        return {
          ...data.resOrder,
          lines: orderInfo.lines
            .map((line) => resLineToCart(line, menu))
            .filter((x) => !!x),
        };
      }
    }

    // Action submitted and currently processing
    // Get order from action request
    const formValue: ResponseOrderWithCartLines = JSON.parse(
      fetcher.formData?.get("orderSummary")?.toString() ?? "",
    );
    return formValue;
  }, [
    menu,
    fetcher.state,
    fetcher.formData,
    fetcher.data,
    unclearedOrderInfo,
    isTableOrdering,
    defaultTableNumber,
    savedCustomer,
    requestedReadyTime,
    loaderDeliveryInfo,
    orderTypeCode,
    clearOrder,
  ]);

  // Cart readonly when time is not valid or if we are currently resetting or inserting a new order, or there are pending payments
  const pendingPayments = !!orderSummary?.pendingPaymentIds?.length;
  const readonlyCart = useMemo(
    () =>
      !timeValid ||
      pendingPayments ||
      (fetcher.state === "submitting" &&
        (!!fetcher.formData?.get("isReset") ||
          !!fetcher.formData?.get("forceInsert"))),
    [fetcher.state, fetcher.formData, timeValid, pendingPayments],
  );

  // Helper function to submit order actions
  const submitOrderAction = ({
    order,
    preventInsert,
    allowSavePersonalData,
    setOrderTypeCode,
    setRequestedTime,
    setDeliveryInfo,
    redirectToPayment,
    errorOnInvalidAsset,
  }: {
    order: ResponseOrderWithCartLines | null;
    preventInsert?: boolean;
    allowSavePersonalData?: boolean;
    setOrderTypeCode?: string;
    setRequestedTime?: string | null;
    setDeliveryInfo?: DeliveryInfo | null;
    redirectToPayment?: boolean;
    errorOnInvalidAsset?: boolean;
  }) => {
    const formData = new FormData();

    let clearCache = false;

    formData.append("orgId", orgId);

    if (errorOnInvalidAsset) {
      formData.append("errorOnInvalidAsset", "true");
    }

    // check for undefined, as null (ASAP) is a valid time to set
    if (setRequestedTime !== undefined) {
      clearCache = true;
      formData.append("setRequestedTime", JSON.stringify(setRequestedTime));
    }

    if (setOrderTypeCode) {
      clearCache = true;
      formData.append("setOrderTypeCode", setOrderTypeCode);
    }

    if (setDeliveryInfo) {
      clearCache = true;
      formData.append("setDeliveryInfo", JSON.stringify(setDeliveryInfo));
    }

    if (preventInsert) {
      formData.append("preventInsert", "true");
    }

    // If the context order has no id, likely we have a new order, or the order in session is no longer valid (e.g. it is already sent)
    if (!order?.id) {
      if (preventInsert) {
        // Insert is prevented, but the order is not allowed to be updated, so we must also prevent update
        formData.append("preventUpdate", "true");
      } else {
        // Force insert a new order since we are not preventing insert
        clearCache = true;
        formData.append("forceInsert", "true");
      }
    }

    if (redirectToPayment) {
      formData.append("redirectToPayment", "true");
    }

    if (order) {
      const orderSummary = recalculateOrder(
        order,
        taxFromCode,
        scheduledPromotions,
      );

      formData.append("orderSummary", JSON.stringify(orderSummary));
    } else {
      clearCache = true;
      formData.append("isReset", "true");

      if (useComoAuth) {
        localStorage.setItem("last-reset", new Date().toISOString());
      }
    }

    if (allowSavePersonalData != undefined) {
      formData.append(
        "allowSavePersonalData",
        allowSavePersonalData.toString(),
      );
    }

    fetcher.submit(formData, {
      action: `/${paramOrderSource}/${site.id}${
        paramTableNumber ? "/" + paramTableNumber : ""
      }?clearCache=${clearCache}`,
      method: "post",
    });
  };

  const setTableNumber = (value: string) => {
    if (readonlyCart) {
      return;
    }
    submitOrderAction({
      order: { ...orderSummary, tableNumber: value },
    });
  };

  const setTableRef = useRef(setTableNumber);
  setTableRef.current = setTableNumber;
  useEffect(() => {
    if (paramTableNumber && orderSummary.tableNumber !== paramTableNumber) {
      setTableRef.current(paramTableNumber);
    }
  }, [orderSummary.tableNumber, paramTableNumber]);

  const getOrderLineById = (id: string) => {
    return orderSummary.lines.find((orderLine) => orderLine.id === id) || null;
  };

  const addOrderLine = (orderLine: CartLine) => {
    if (readonlyCart) {
      return;
    }

    if (
      !orderLine.item.product.children.length &&
      !orderLine.item.children.length
    ) {
      const combineWith = orderSummary.lines.find(
        (x) =>
          x.item.productCode == orderLine.item.productCode &&
          !x.item.children.length &&
          // Lines in a bundle deal stay one unit each
          !lineInBundle(x),
      );
      if (combineWith) {
        updateOrderLine({
          ...combineWith,
          item: {
            ...combineWith.item,
            qty: combineWith.item.qty + orderLine.item.qty,
          },
        });
        return;
      }
    }

    const newOrderLines = [...orderSummary.lines, orderLine];
    submitOrderAction({ order: { ...orderSummary, lines: newOrderLines } });
  };

  const removeOrderLine = (id: string) => {
    if (readonlyCart) {
      return;
    }
    const newOrderLines = orderSummary.lines.filter(
      (orderLine) => orderLine.id !== id,
    );
    submitOrderAction({ order: { ...orderSummary, lines: newOrderLines } });
  };

  const removeOrderLines = (ids: string[]) => {
    if (readonlyCart) {
      return;
    }
    const newOrderLines = orderSummary.lines.filter(
      (orderLine) => !ids.includes(orderLine.id),
    );
    submitOrderAction({ order: { ...orderSummary, lines: newOrderLines } });
  };

  const addOrderLines = (lines: CartLine[], removeIds: string[] = []) => {
    if (readonlyCart) {
      return;
    }
    const newOrderLines = [
      ...orderSummary.lines.filter((x) => !removeIds.includes(x.id)),
      ...lines,
    ];
    submitOrderAction({ order: { ...orderSummary, lines: newOrderLines } });
  };

  const updateOrderLine = (updatedOrderLine: CartLine) => {
    if (readonlyCart) {
      return;
    }
    const newOrderLines = orderSummary.lines.map((orderLine) =>
      orderLine.id === updatedOrderLine.id ? updatedOrderLine : orderLine,
    );

    submitOrderAction({
      order: { ...orderSummary, lines: newOrderLines },
    });
  };

  const redeemPromotion = (dealKey: string) => {
    if (readonlyCart) {
      return;
    }
    submitOrderAction({
      order: {
        ...orderSummary,
        redemptionKey: dealKey,
      },
      errorOnInvalidAsset: true,
    });
  };

  const redeemPromotionCode = (code: string) => {
    if (readonlyCart) {
      return;
    }
    submitOrderAction({
      order: {
        ...orderSummary,
        promoCode: code.trim() === "" ? null : code.trim(),
      },
      errorOnInvalidAsset: true,
    });
  };

  const removePromotion = () => {
    submitOrderAction({
      order: { ...orderSummary, redemptionKey: null },
    });
  };

  const removePromotionCode = () => {
    submitOrderAction({
      order: { ...orderSummary, promoCode: null },
    });
  };

  const setCustomerData = (
    customer: BaseUser,
    allowSavePersonalData?: boolean,
  ) => {
    submitOrderAction({
      order: { ...orderSummary, customer },
      allowSavePersonalData,
    });
  };

  // ── Unified store finder ────────────────────────────────────────────
  // Everything below is inert unless the org's template enables the finder.
  // The gate is read once, here, rather than at each call site, so a caller
  // that forgets to check cannot open a finder the merchant never turned on.
  const { template } = useContext(ThemeContext);
  const finderEnabled = storeFinderEnabled(template);

  // The experience the finder is showing: the current one, until the customer switches it in place ("Looking for
  // catering?") or it's opened for another
  const [finderExperience, setFinderExperience] =
    useState<OrderSource>(orderSource);
  const openStoreFinder = (
    initialFulfilment?: "PICKUP" | "DELIVERY",
    experience: OrderSource = orderSource,
  ) => {
    setFinderExperience(experience);
    if (!finderEnabled) return;
    if (orgSitesFetcher.state === "idle" && !orgSitesFetcher.data && orgId) {
      orgSitesFetcher.load("/api/orgSites");
    }
    if (initialFulfilment) setStoreFinderFulfilmentOverride(initialFulfilment);
    setShowStoreFinder(true);
  };

  const openTimeSheet = () => {
    if (!finderEnabled) return;
    // Opened on its own: the order's current type and address are the truth.
    setPendingTimeChoice(null);
    setShowTimeSheet(true);
  };

  const closeStoreFinder = () => {
    setShowStoreFinder(false);
    setStoreFinderFulfilmentOverride(null);
  };

  // Reuses the same DeliveryInfo contract ChangeOrderModal.handleSave produces,
  // so a store chosen in the finder and one chosen in the modal write the
  // session identically.
  const deliveryInfoFromFinderAddress = (deliveryAddress?: {
    formattedAddress: string;
    latitude: number;
    longitude: number;
  }): DeliveryInfo | null =>
    deliveryAddress
      ? {
          address: {
            // Street line where we have it; the finder hands back the formatted
            // address, so fall back to that until it carries components too.
            address1: deliveryAddress.formattedAddress,
            address2: "",
            city: "",
            countryId: null,
            postalCode: "",
            state: "",
            latitude: deliveryAddress.latitude,
            longitude: deliveryAddress.longitude,
          },
        }
      : null;

  const handleStoreFinderSelect = ({
    site: chosenSite,
    fulfilment,
    deliveryAddress,
  }: {
    site: SiteListObject;
    fulfilment: "PICKUP" | "DELIVERY";
    deliveryAddress?: {
      formattedAddress: string;
      latitude: number;
      longitude: number;
    };
  }) => {
    const chosenOrderType = findOrderTypeForFulfilment(
      chosenSite.orderTypes,
      fulfilment,
      finderExperience,
    );
    const deliveryInfo = deliveryInfoFromFinderAddress(deliveryAddress);

    if (chosenSite.id !== site.id || finderExperience !== orderSource) {
      // Different store. Persist the chosen delivery address through the
      // existing mechanism BEFORE navigating: deliveryInfo lives in the shared
      // (__order) session rather than a site-scoped one, so the destination
      // site's loader reads it back. The write uses the CURRENT order type on
      // purpose — it should add the address without mutating the type of an
      // order the customer is leaving behind. The destination's type travels on
      // the URL instead.
      if (deliveryInfo && orderType) {
        setOrderTypeInfo(
          orderType,
          orderSummary.requestedReadyTime ?? null,
          deliveryInfo,
          false,
        );
      }
      navigate(
        urlGen("Order", {
          // Another experience's store goes to that experience (its cart is waiting there)
          orderSource: finderExperience,
          siteId: chosenSite.id,
          tableNumber: paramTableNumber,
          orderTypeCode: chosenOrderType?.code ?? null,
        }),
      );
      closeStoreFinder();
      return;
    }

    // Same store: apply the chosen fulfilment, then ask for the time.
    //
    // The hand-off goes to ChooseTimeSheet, never ChangeOrderModal. The finder
    // has just captured store, fulfilment and a geocoded address; the order-type
    // modal asks all of it again and re-derives delivery info from its own
    // inputs, discarding the coordinates the finder resolved. The time sheet
    // only ever writes a time.
    closeStoreFinder();
    if (chosenOrderType) {
      setOrderTypeInfo(
        chosenOrderType,
        orderSummary.requestedReadyTime ?? null,
        deliveryInfo,
        false,
      );
      // Carry the choice to the sheet, so its save commits THIS fulfilment
      // rather than the one context still reports.
      setPendingTimeChoice({ orderType: chosenOrderType, deliveryInfo });

      // Drop ?orderTypeCode= from the URL. The loader treats that param as
      // authoritative and re-applies it to the session on EVERY pass, so a stale
      // one silently overrides every later change — the order and its price move
      // to pickup while the param keeps forcing the type back to delivery. The
      // order-type modal already cleared it on save; the finder has to as well.
      if (searchParams.has("orderTypeCode")) {
        setSearchParams(
          (params) => {
            params.delete("orderTypeCode");
            return params;
          },
          { replace: true, preventScrollReset: true },
        );
      }
    }
    setShowTimeSheet(true);
  };

  const setOrderTypeInfo = (
    newOrderType: OrderType,
    requestedTime: string | null,
    deliveryInfo: DeliveryInfo | null,
    isReset: boolean,
  ) => {
    submitOrderAction({
      // Only set order type if it is new
      setOrderTypeCode:
        newOrderType.code === orderTypeCode ? undefined : newOrderType.code,
      setRequestedTime: requestedTime,
      setDeliveryInfo: deliveryInfo,
      order: isReset
        ? null
        : { ...orderSummary, requestedReadyTime: requestedTime, deliveryInfo },
      // If no order exists, no need to insert one yet, as the requested time is stored in session
      preventInsert: true,
    });
  };

  const reset = () => {
    submitOrderAction({
      order: null,
    });
  };

  const refresh = () => {
    submitOrderAction({
      order: orderSummary,
    });
  };

  const setGroupOrder = (groupOrder: boolean, redirectToPayment: boolean) => {
    if (readonlyCart) {
      return;
    }
    submitOrderAction({
      order: { ...orderSummary, groupOrder },
      redirectToPayment,
    });
  };

  const promotionsRequiringSelection = useMemo(
    () => getPromotionsRequiringSelection(orderSummary),
    [orderSummary],
  );

  const orderType = useMemo(
    () => getOrderType(orderTypeCode, site.orderTypes),
    [orderTypeCode, site.orderTypes],
  );

  // Catering books ahead: the time sheet says how far, and never picks a time for the customer
  const cateringLeadNote = useMemo(() => {
    if (orderSource !== OrderSource.CATERING) {
      return undefined;
    }
    const firstSlot = availableTimeSlots.find((x): x is string => !!x);
    const zonedFirst = firstSlot ? toZonedTime(firstSlot, site.timeZone) : null;
    const zonedNow = toZonedTime(new Date(), site.timeZone);
    return (
      leadNoteFor({
        settings: settingsFor(site.serviceSettings, orderType?.code),
        kindLabel: `Catering ${
          orderType?.fulfillmentType === OrderFulfillmentType.DELIVERY
            ? "delivery"
            : "pickup"
        }`,
        storeName: site.siteName,
        earliestTodayLabel:
          zonedFirst && isSameDay(zonedFirst, zonedNow)
            ? formatTime(zonedFirst)
            : null,
      }) ?? undefined
    );
  }, [orderSource, availableTimeSlots, site, orderType]);

  // If order is not attached to the authenticated user, detect this and attach it. Only attempt this once however.
  const { authenticatedUser } = useContext(AuthContext);
  const orderRefreshAttempted = useRef(false);
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  const orderNeedsRefresh =
    !clearOrder &&
    unclearedOrderInfo?.customerId == null &&
    !!authenticatedUser?.id &&
    fetcher.state === "idle";
  useEffect(() => {
    if (!orderRefreshAttempted.current && orderNeedsRefresh) {
      orderRefreshAttempted.current = true;
      console.warn("Order missing customer id. Refreshing...");
      refreshRef.current();
    }
  }, [orderNeedsRefresh]);

  // Get the user to confirm an order time when asap is not available for the selected order type,
  //   and a requested time has not already been set in the session
  const timeNeedsSelecting =
    !searchParams.has("allowImplicitSettings") &&
    orderSummary.id === "" &&
    sessionRequestedTime == null &&
    !asapAvailable;

  const location = useLocation();
  const pathEnd = getRouteEnd(location.pathname);
  // Catering always books ahead, so it never pops this on arrival: customers browse first, and the store bar's
  // "Choose a time" and checkout ask for the time instead
  const autoShowOrderTypeModal =
    timeNeedsSelecting &&
    orderSource !== OrderSource.CATERING &&
    !["cancelled", "checkout", "declined", "success", "payment"].includes(
      pathEnd,
    );

  return (
    <OrderContext.Provider
      value={{
        orderSummary,
        orderSource,
        orderTypeCode,
        orderType,
        deliveryInfo: loaderDeliveryInfo,
        isTableOrdering,
        setTableNumber,
        getOrderLineById,
        addOrderLine,
        removeOrderLine,
        removeOrderLines,
        addOrderLines,
        updateOrderLine,
        reset,
        refresh,
        setCustomerData,
        redeemPromotion,
        removePromotion,
        redeemPromotionCode,
        removePromotionCode,
        setOrderTypeInfo,
        numTimeSlots: availableTimeSlots.length,
        firstAvailableSlot:
          availableTimeSlots.find((x): x is string => !!x) ?? null,
        successPageOrderInfo: unclearedOrderInfo,
        openOrderTimeModal: () => setShowOrderTimeModal(true),
        openStoreFinder,
        openTimeSheet,
        promotionsRequiringSelection,
        setGroupOrder,
        orderLoading: fetcher.state === "submitting",
        orderIdle: fetcher.state === "idle",
        setConfirmingAction,
        readonlyCart,
        asapWait,
        signBackInRequired,
        sessionRequestedTime,
        savedCart,
        otherExperienceCart,
      }}
    >
      {children}
      {/** Change Order modal */}
      <ChangeOrderModal
        visible={showOrderTimeModal || autoShowOrderTypeModal}
        onClose={(isReset, isSave) => {
          if (autoShowOrderTypeModal && !isSave) {
            // Something to hide the modal
            setSearchParams((params) => {
              params.set("allowImplicitSettings", "true");
              return params;
            });
          }

          if (isReset) {
            navigate(
              urlGen("Order", {
                orderSource,
                siteId: site.id,
                menuCode: paramMenuCode,
                tableNumber: paramTableNumber,
              }),
            );
          } else if (isSave && searchParams.has("orderTypeCode")) {
            setSearchParams((params) => {
              params.delete("orderTypeCode");
              return params;
            });
          }
          setShowOrderTimeModal(false);
        }}
      />
      {/* "When would you like it?" — the direct time picker, opened from the
          store bar's ETA chip and after choosing the current store in the
          finder. Saves through the same setOrderTypeInfo path as the order-type
          modal's time step, so re-pricing and cache behaviour are identical. */}
      {finderEnabled && (
        <ChooseTimeSheet
          visible={showTimeSheet}
          onClose={() => {
            setPendingTimeChoice(null);
            setShowTimeSheet(false);
          }}
        >
          <ChooseTime
            visible={showTimeSheet}
            availableTimeSlots={availableTimeSlots}
            // Catering: the order's time is only a placeholder until the customer picks one, so nothing is selected
            requestedReadyTime={
              orderSource === OrderSource.CATERING &&
              sessionRequestedTime == null
                ? null
                : orderSummary.requestedReadyTime
            }
            asapWait={asapWait}
            saving={fetcher.state !== "idle"}
            leadNote={cateringLeadNote}
            noAutoSelect={orderSource === OrderSource.CATERING}
            onSave={(value) => {
              // A choice just made in the finder wins over context, which still
              // describes the previous one until the post round-trips. Without
              // this, saving a time re-applied the OLD fulfilment and address —
              // the pill never moved and the delivery fee came back.
              const typeToSave = pendingTimeChoice?.orderType ?? orderType;
              const deliveryToSave = pendingTimeChoice
                ? pendingTimeChoice.deliveryInfo
                : (orderSummary.deliveryInfo ?? loaderDeliveryInfo);

              if (typeToSave) {
                setOrderTypeInfo(typeToSave, value, deliveryToSave, false);
              }
              setPendingTimeChoice(null);
              setShowTimeSheet(false);
            }}
          />
        </ChooseTimeSheet>
      )}
      {/* Only mounted when the org's template enables the finder, so the
          overlay cannot be reached even if a call site forgets the gate. */}
      {finderEnabled && (
        <StoreFinderShell
          visible={showStoreFinder}
          onClose={closeStoreFinder}
          title={
            finderExperience === OrderSource.CATERING
              ? "Choose a catering store"
              : undefined
          }
          wide={finderExperience === OrderSource.CATERING}
        >
          {orgSitesFetcher.state === "loading" && !orgSitesFetcher.data ? (
            <div style={{ padding: 24, textAlign: "center" }}>
              Loading stores…
            </div>
          ) : (
            <StoreFinder
              orgId={orgId}
              sites={orgSitesFetcher.data?.sites ?? []}
              prevSiteId={site.id}
              orderSource={finderExperience}
              deliverySearch={deliverySearchEnabled(template)}
              // Table ordering stays at its table; everyday and catering can switch in place
              onExperienceChange={
                orderSource === OrderSource.TABLE_ORDERING
                  ? undefined
                  : setFinderExperience
              }
              initialFulfilment={
                storeFinderFulfilmentOverride ??
                fulfilmentFromOrderType(orderType)
              }
              // Pre-fill the delivery tab when the order already carries an
              // address, so the customer does not retype it on every change.
              initialAddress={
                loaderDeliveryInfo?.address?.address1 &&
                loaderDeliveryInfo.address.latitude != null &&
                loaderDeliveryInfo.address.longitude != null
                  ? {
                      formattedAddress: loaderDeliveryInfo.address.address1,
                      latitude: loaderDeliveryInfo.address.latitude,
                      longitude: loaderDeliveryInfo.address.longitude,
                    }
                  : undefined
              }
              onSelect={handleStoreFinderSelect}
            />
          )}
        </StoreFinderShell>
      )}
      <ConfirmActionModal
        visible={!!confirmingAction}
        onClose={() => setConfirmingAction(null)}
        onConfirm={() => {
          confirmingAction && confirmingAction?.action();
        }}
        icon={confirmingAction?.icon}
        title={confirmingAction?.title}
        body={confirmingAction?.body}
      />
    </OrderContext.Provider>
  );
};

export default OrderContext;
